import { actionTypes } from '../actions/types';

type AppActions = import('../actions/types').AppActions;

export interface LoaderReducer {
    [k: string]: boolean;
}

export const initialState: LoaderReducer = {
    isLoggedIn: false,

    /* Fetching */
    isFetchingUserSession: false,
    isFetchingUserContext: false,
    isFetchingCalculatorCache: false,
    isFetchingOngoingApplications: false,
    isFetchingApplication: false,
    isFetchingProducts: false,
    isFetchingProductInfo: false,
    isFetchingVehicleInfo: false,
    isFetchingKyc: false,
    isFetchingConsent: false,
    isFetchingSblConsent: false,

    /* Fetched */
    hasFetchedUserSession: false,
    hasFetchedUserContext: false,
    hasFetchedCalculatorCache: false,
    hasFetchedOngoingApplications: false,
    hasFetchedApplication: false,
    hasFetchedProducts: false,
    hasFetchedProductInfo: false,
    hasFetchedVehicleInfo: false,
    hasFetchedConsent: false,
    hasFetchedSblConsent: false,

    /* Sending */
    isUpdatingCalculatorCache: false,
    isSendingApplication: false,
    isSendingFeedback: false,
    isUpdatingApplication: false,
    isCompletingApplication: false,
    isUpgradingApplication: false,

    /* Sent */
    hasUpdatedCalculatorCache: false,
    hasSentApplication: false,
    hasSentFeedback: false,
    hasUpdatedApplication: false,
    hasCompletedApplication: false,

    /* Creating */
    isCreatingSblConsent: false,

    /* Failed */
    fetchProductInfoFailed: false,
    fetchCalculatorCacheFailed: false,
    fetchApplicationFailed: false,
    updateApplicationFailed: false,
    createSblConsentFailed: false,

    /* Forms */
    hasFormLoanSelection: false,
    hasFormLoanInfo: false,
    hasFormApplicationMainApplicant: false,
    hasFormApplicationCoApplicant: false,
    isUpgradingOngoingApplication: false,

    /* Updateing initial steps for completion process */
    hasNotYetUpdatedCompletionSteps: true,
};

export const loaderReducer = (state = initialState, action: AppActions = {} as AppActions): LoaderReducer => {
    switch (action.type) {
        // Landing Page (All Applications!)
        case actionTypes.FETCH_ONGOING_APPLICATIONS_STARTED: {
            return {
                ...state,
                isFetchingOngoingApplications: true,
            };
        }
        case actionTypes.FETCH_ONGOING_APPLICATIONS_FULFILLED: {
            return {
                ...state,
                isFetchingOngoingApplications: false,
                hasFetchedOngoingApplications: true,
            };
        }
        case actionTypes.FETCH_ONGOING_APPLICATIONS_FAILED: {
            return {
                ...state,
                isFetchingOngoingApplications: false,
            };
        }

        // All Products
        case actionTypes.FETCH_PRODUCTS_STARTED: {
            return {
                ...state,
                isFetchingProducts: true,
            };
        }
        case actionTypes.FETCH_PRODUCTS_FULFILLED: {
            return {
                ...state,
                isFetchingProducts: false,
                hasFetchedProducts: true,
            };
        }
        case actionTypes.FETCH_PRODUCTS_FAILED: {
            return {
                ...state,
                isFetchingProducts: false,
            };
        }

        // Product Info
        case actionTypes.FETCH_PRODUCT_INFO_STARTED: {
            return {
                ...state,
                isFetchingProductInfo: true,
                fetchProductInfoFailed: false,
            };
        }
        case actionTypes.FETCH_PRODUCT_INFO_FULFILLED: {
            return {
                ...state,
                isFetchingProductInfo: false,
                hasFetchedProductInfo: true,
                fetchProductInfoFailed: false,
            };
        }
        case actionTypes.FETCH_PRODUCT_INFO_FAILED: {
            return {
                ...state,
                isFetchingProductInfo: false,
                fetchProductInfoFailed: false,
            };
        }
        case actionTypes.FETCH_PRODUCT_INFO_INVALID: {
            return {
                ...state,
                fetchProductInfoFailed: true,
            };
        }

        // Vehicle Registration Info
        case actionTypes.FETCH_VEHICLE_INFO_STARTED: {
            return {
                ...state,
                isFetchingVehicleInfo: true,
            };
        }
        case actionTypes.FETCH_VEHICLE_INFO_FULFILLED: {
            return {
                ...state,
                isFetchingVehicleInfo: false,
                hasFetchedVehicleInfo: true,
            };
        }
        case actionTypes.FETCH_VEHICLE_INFO_FAILED: {
            return {
                ...state,
                isFetchingVehicleInfo: false,
                hasFetchedVehicleInfo: false,
            };
        }
        case actionTypes.UPDATE_PRODUCT: {
            return {
                ...state,
                // Reset vehicle lookup when product is updated.
                hasFetchedVehicleInfo: false,
            };
        }

        // User Session
        case actionTypes.FETCH_USER_SESSION_STARTED: {
            return {
                ...state,
                isFetchingUserSession: true,
            };
        }
        case actionTypes.FETCH_USER_SESSION_FULFILLED: {
            return {
                ...state,
                isFetchingUserSession: false,
                hasFetchedUserSession: true,
                isLoggedIn: action.payload.valid,
            };
        }
        case actionTypes.FETCH_USER_SESSION_FAILED: {
            return {
                ...state,
                isFetchingUserSession: false,
            };
        }

        // User Context
        case actionTypes.FETCH_USER_CONTEXT_STARTED: {
            return {
                ...state,
                isFetchingUserContext: true,
            };
        }
        case actionTypes.FETCH_USER_CONTEXT_FULFILLED: {
            return {
                ...state,
                isFetchingUserContext: false,
                hasFetchedUserContext: true,
            };
        }
        case actionTypes.FETCH_USER_CONTEXT_FAILED: {
            return {
                ...state,
                isFetchingUserContext: false,
            };
        }

        // Fetch Calculator Cache
        case actionTypes.FETCH_CALCULATOR_CACHE_STARTED: {
            return {
                ...state,
                isFetchingCalculatorCache: true,
            };
        }
        case actionTypes.FETCH_CALCULATOR_CACHE_FULFILLED: {
            return {
                ...state,
                isFetchingCalculatorCache: false,
                hasFetchedCalculatorCache: true,
            };
        }
        case actionTypes.FETCH_CALCULATOR_CACHE_FAILED: {
            return {
                ...state,
                isFetchingCalculatorCache: false,
                fetchCalculatorCacheFailed: true,
            };
        }

        // Update Calculator Cache
        case actionTypes.UPDATE_CALCULATOR_CACHE_STARTED: {
            return {
                ...state,
                isUpdatingCalculatorCache: true,
            };
        }
        case actionTypes.UPDATE_CALCULATOR_CACHE_FULFILLED: {
            return {
                ...state,
                isUpdatingCalculatorCache: false,
                hasUpdatedCalculatorCache: true,
            };
        }
        case actionTypes.UPDATE_CALCULATOR_CACHE_FAILED: {
            return {
                ...state,
                isUpdatingCalculatorCache: false,
            };
        }

        // Application Status
        case actionTypes.FETCH_APPLICATION_STARTED: {
            return {
                ...state,
                isFetchingApplication: true,
                fetchApplicationFailed: false,
            };
        }
        case actionTypes.FETCH_APPLICATION_FULFILLED: {
            return {
                ...state,
                isFetchingApplication: false,
                hasFetchedApplication: true,
                fetchApplicationFailed: false,
            };
        }
        case actionTypes.FETCH_APPLICATION_FAILED: {
            return {
                ...state,
                isFetchingApplication: false,
                fetchApplicationFailed: true,
            };
        }

        // KYC
        case actionTypes.FETCH_CUSTOMER_KYC_STARTED: {
            return {
                ...state,
                isFetchingKyc: true,
            };
        }
        case actionTypes.FETCH_CUSTOMER_KYC_FULFILLED: {
            return {
                ...state,
                isFetchingKyc: false,
            };
        }
        case actionTypes.FETCH_CUSTOMER_KYC_FAILED: {
            return {
                ...state,
                isFetchingKyc: false,
            };
        }

        // Consent
        case actionTypes.FETCH_CONSENT_STARTED: {
            return {
                ...state,
                isFetchingConsent: true,
            };
        }
        case actionTypes.FETCH_CONSENT_FULFILLED: {
            return {
                ...state,
                isFetchingConsent: false,
                hasFetchedConsent: true,
            };
        }
        case actionTypes.FETCH_CONSENT_FAILED: {
            return {
                ...state,
                isFetchingConsent: false,
            };
        }

        // Send Application
        case actionTypes.SEND_APPLICATION_STARTED: {
            return {
                ...state,
                isSendingApplication: true,
            };
        }
        case actionTypes.SEND_APPLICATION_FULFILLED: {
            return {
                ...state,
                isSendingApplication: false,
                hasSentApplication: true,
            };
        }
        case actionTypes.SEND_APPLICATION_FAILED: {
            return {
                ...state,
                isSendingApplication: false,
            };
        }

        // Send Feedback
        case actionTypes.SEND_FEEDBACK_STARTED: {
            return {
                ...state,
                isSendingFeedback: true,
            };
        }
        case actionTypes.SEND_FEEDBACK_FULFILLED: {
            return {
                ...state,
                isSendingFeedback: false,
                hasSentFeedback: true,
            };
        }
        case actionTypes.SEND_FEEDBACK_FAILED: {
            return {
                ...state,
                isSendingFeedback: false,
            };
        }

        // Update Application
        case actionTypes.UPDATE_APPLICATION_STARTED: {
            return {
                ...state,
                isUpdatingApplication: true,
                updateApplicationFailed: false,
            };
        }
        case actionTypes.UPDATE_APPLICATION_FULFILLED: {
            return {
                ...state,
                isUpdatingApplication: false,
                hasUpdatedApplication: true,
                updateApplicationFailed: false,
            };
        }
        case actionTypes.UPDATE_APPLICATION_FAILED: {
            return {
                ...state,
                isUpdatingApplication: false,
                updateApplicationFailed: true,
            };
        }

        // Form updates
        case actionTypes.FORM_LOAN_INFO: {
            return {
                ...state,
                hasFormLoanInfo: true,
            };
        }
        case actionTypes.FORM_APPLICATION_MAIN_APPLICANT: {
            return {
                ...state,
                hasFormApplicationMainApplicant: true,
            };
        }
        case actionTypes.FORM_APPLICATION_CO_APPLICANT: {
            return {
                ...state,
                hasFormApplicationCoApplicant: true,
            };
        }
        case actionTypes.CREATE_SBL_CONSENT_STARTED: {
            return {
                ...state,
                isCreatingSblConsent: true,
                createSblConsentFailed: false,
            };
        }
        case actionTypes.CREATE_SBL_CONSENT_FULFILLED: {
            return {
                ...state,
                isCreatingSblConsent: false,
                createSblConsentFailed: false,
            };
        }
        case actionTypes.CREATE_SBL_CONSENT_FAILED: {
            return {
                ...state,
                isCreatingSblConsent: false,
                createSblConsentFailed: true,
            };
        }
        case actionTypes.FETCH_SBL_CONSENT_STARTED: {
            return {
                ...state,
                isFetchingSblConsent: true,
            };
        }
        case actionTypes.FETCH_SBL_CONSENT_FULFILLED: {
            return {
                ...state,
                isFetchingSblConsent: false,
                hasFetchedSblConsent: true,
            };
        }
        case actionTypes.FETCH_SBL_CONSENT_FAILED: {
            return {
                ...state,
                isFetchingSblConsent: false,
                hasFetchedSblConsent: false,
            };
        }
        case actionTypes.UPGRADE_ONGOING_APPLICATION_STARTED: {
            return {
                ...state,
                isUpgradingOngoingApplication: true,
            };
        }
        case actionTypes.UPGRADE_ONGOING_APPLICATION_FAILED: {
            return {
                ...state,
                isUpgradingOngoingApplication: false,
            };
        }
        case actionTypes.UPGRADE_ONGOING_APPLICATION_SUCCESS: {
            return {
                ...state,
                isUpgradingOngoingApplication: false,
            };
        }
        case actionTypes.UPDATE_STEPS_COMPLETION: {
            return {
                ...state,
                hasNotYetUpdatedCompletionSteps: false,
            };
        }

        default:
            return state;
    }
};
